"use client";

export type CheckState = "done" | "todo" | "note";

export interface CheckListItem {
  key: string;
  state: CheckState;
  text: string;
}

const MARKS: Record<CheckState, string> = {
  done: "✓",
  todo: "○",
  note: "!",
};

const TONES: Record<CheckState, string> = {
  done: "text-primary",
  todo: "text-muted-foreground",
  note: "text-destructive",
};

/**
 * A checklist whose state is spoken as well as drawn: the mark is decorative and each
 * row starts with a visually hidden state word, so a screen reader hears
 * "Done: …", "To do: …" or "Check: …" rather than a bare symbol.
 */
export function CheckList({
  items,
  stateLabels,
  label,
}: {
  items: readonly CheckListItem[];
  stateLabels: Record<CheckState, string>;
  label: string;
}) {
  return (
    <ul aria-label={label} className="space-y-1.5">
      {items.map((item) => (
        <li className="flex gap-2 text-foreground text-sm" key={item.key}>
          <span
            aria-hidden="true"
            className={`w-4 shrink-0 text-center font-semibold ${TONES[item.state]}`}
          >
            {MARKS[item.state]}
          </span>
          <span>
            <span className="sr-only">{stateLabels[item.state]}: </span>
            {item.text}
          </span>
        </li>
      ))}
    </ul>
  );
}
